/**
 * The pinned-views card, with the line that starts #1432: "Send on a
 * schedule".
 *
 * Built to threads/cycle-3-report-library/mockup.html, panel 1. A deviation
 * from the mockup is a bug (docs/bible/design-to-code-handoff.md).
 *
 * Generic on purpose. It reads and writes the ONE saved-view store
 * (services/savedViews.ts, D6) through /api/saved-views?surface=…, so any
 * list that adopts it gets pinning and scheduling together rather than
 * growing a second store — the failure migration 571 existed to undo.
 *
 * A view already on a schedule shows the schedule in words under its name
 * ("Every Monday at 7 AM · Corbin"), because the office's real question about
 * a saved list is "is this the one that emails me".
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pin, X } from "lucide-react";
import { api } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/components/ui/app-dialogs";
import { COMPLIANCE_COPY as C } from "@/pages/compliance/compliance-copy";
import { ScheduleDialog, type SavedViewSchedule } from "./schedule-dialog";

const S = C.section.schedules;

interface ServerSavedView {
    id: string;
    name: string;
    params?: Record<string, unknown> | null;
}

interface Recipient {
    id: string;
    name: string;
    email: string;
}

interface Props {
    /** A schedulable surface: obligations, filings, authorities. */
    surface: string;
    /** The filters the screen is showing right now, or null when this tab cannot be pinned. */
    currentParams: Record<string, unknown> | null;
    /** Put a pinned view's filters back on the screen. */
    onApply: (params: Record<string, unknown>) => void;
}

export function PinnedViewsCard({ surface, currentParams, onApply }: Props) {
    const qc = useQueryClient();
    const [scheduling, setScheduling] = useState<{
        viewId: string;
        name: string;
        existing: SavedViewSchedule | null;
    } | null>(null);

    const viewsQ = useQuery({
        queryKey: ["saved-views", surface],
        queryFn: () => api.get<{ views: ServerSavedView[] }>(`/api/saved-views?surface=${surface}`),
    });
    const schedulesQ = useQuery({
        queryKey: ["saved-view-schedules"],
        queryFn: () => api.get<{ schedules: SavedViewSchedule[] }>("/api/saved-view-schedules"),
    });
    const peopleQ = useQuery({
        queryKey: ["saved-view-schedules", "recipients"],
        queryFn: () => api.get<{ recipients: Recipient[] }>("/api/saved-view-schedules/recipients"),
        staleTime: 300_000,
    });

    const views = viewsQ.data?.views ?? [];
    const scheduleByView = new Map((schedulesQ.data?.schedules ?? []).map((s) => [s.savedViewId, s]));
    const nameById = new Map((peopleQ.data?.recipients ?? []).map((p) => [p.id, p.name]));

    const pin = useMutation({
        mutationFn: (name: string) => api.post(`/api/saved-views?surface=${surface}`, { name, params: currentParams }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["saved-views", surface] });
            toast.success(S.card.pinned);
        },
        onError: () => toast.error(S.card.pinFailed),
    });

    const unpin = useMutation({
        mutationFn: (id: string) => api.delete(`/api/saved-views/${id}?surface=${surface}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["saved-views", surface] });
            toast.success(S.card.removed);
        },
        onError: () => toast.error(S.card.pinFailed),
    });

    function pinCurrent() {
        if (!currentParams) {
            toast.error(S.card.cannotPin);
            return;
        }
        void promptDialog({ title: S.card.pinPrompt }).then((name) => {
            const trimmed = name?.trim();
            if (trimmed) pin.mutate(trimmed);
        });
    }

    return (
        <div className="w-full max-w-[276px] rounded-xl border border-border bg-card">
            <div className="flex flex-row items-center justify-between px-4 pb-2 pt-4">
                <div className="flex items-center gap-1 text-xs font-semibold">
                    <Pin className="h-3.5 w-3.5" /> {S.card.title}
                </div>
                <button
                    type="button"
                    className="h-6 px-2 text-xs font-semibold text-primary disabled:opacity-50"
                    onClick={pinCurrent}
                    disabled={pin.isPending}
                >
                    {S.card.pin}
                </button>
            </div>
            <div className="space-y-2 px-4 pb-4 pt-1 text-sm">
                {views.length === 0 && <p className="text-[11px] text-foreground/60">{S.card.empty}</p>}
                {views.map((v) => {
                    const sched = scheduleByView.get(v.id) ?? null;
                    const who =
                        sched?.recipientUserIds
                            .map((id) => nameById.get(id))
                            .filter(Boolean)
                            .join(", ") ?? "";
                    return (
                        <div key={v.id}>
                            <div className="flex items-center justify-between gap-1">
                                <button
                                    type="button"
                                    className="flex-1 truncate text-left hover:underline"
                                    onClick={() => onApply((v.params ?? {}) as Record<string, unknown>)}
                                >
                                    {v.name}
                                </button>
                                <button
                                    type="button"
                                    aria-label={S.stop}
                                    className="text-foreground/60 hover:text-foreground"
                                    onClick={() => {
                                        void confirmDialog(S.card.removeAsk).then((ok) => {
                                            if (ok) unpin.mutate(v.id);
                                        });
                                    }}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px]">
                                {sched ? (
                                    <>
                                        <span className="rounded border border-border bg-muted px-1.5 text-foreground/80">
                                            {who ? S.link.summary(sched.description, who) : sched.description}
                                        </span>
                                        <button
                                            type="button"
                                            className="font-semibold text-primary"
                                            onClick={() =>
                                                setScheduling({ viewId: v.id, name: v.name, existing: sched })
                                            }
                                        >
                                            {S.change}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        className="font-semibold text-primary"
                                        onClick={() => setScheduling({ viewId: v.id, name: v.name, existing: null })}
                                    >
                                        {S.link.setUp}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {scheduling && (
                <ScheduleDialog
                    open
                    onClose={() => setScheduling(null)}
                    savedViewId={scheduling.viewId}
                    savedViewName={scheduling.name}
                    existing={scheduling.existing}
                />
            )}
        </div>
    );
}
